import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { fetchClient, updateClient, deleteClient, saveMesures, uploadPhoto } from "@/lib/data";
import { api, API, getToken } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import {
  visibleSections, buildMesuresMessage, whatsappUrl, statutLabel, STATUTS, hasAnyMesure,
} from "@/lib/measurements";
import { PhoneInput } from "@/components/PhoneInput";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Trash2, Loader2, Send, Copy, Camera, Image as ImageIcon, Plus,
  X, Download, Shirt, MoveVertical, Flower2, Info, Ruler, Package, Check, Clock, Wallet, BellRing,
} from "lucide-react";

const ICONS = { shirt: Shirt, "move-vertical": MoveVertical, "flower-2": Flower2 };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tailleur } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { client } = await fetchClient(id);
      setClient(client);
    } catch {
      toast.error("Client introuvable");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !client) {
    return <div className="min-h-screen bg-tp-bg flex items-center justify-center"><Loader2 className="animate-spin text-tp-green" /></div>;
  }

  const doDelete = async () => {
    await deleteClient(id);
    toast.success("Client supprimé");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-tp-bg pb-8">
      {/* Header */}
      <div className="bg-tp-green text-white px-4 pt-5 pb-5">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-head font-bold text-xl truncate">{client.nom}</h1>
            <p className="text-white/70 text-sm truncate">{client.telephone || "Pas de numéro"} · {client.sexe === "F" ? "Femme" : "Homme"}</p>
          </div>
          <button data-testid="delete-client-btn" onClick={() => setConfirmDel(true)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <Trash2 size={19} />
          </button>
        </div>
        <button
          data-testid="whatsapp-share-btn"
          onClick={() => setShareOpen(true)}
          className="mt-4 w-full h-[52px] rounded-full bg-tp-whatsapp hover:brightness-105 text-white font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <Send size={20} /> Envoyer sur WhatsApp
        </button>
      </div>

      <Tabs defaultValue="mesures" className="w-full">
        <TabsList className="w-full grid grid-cols-4 bg-white rounded-none h-14 p-0 border-b border-gray-200 sticky top-0 z-10">
          <TabTrig value="infos" icon={Info} label="Infos" />
          <TabTrig value="mesures" icon={Ruler} label="Mesures" />
          <TabTrig value="photos" icon={ImageIcon} label="Photos" />
          <TabTrig value="commandes" icon={Package} label="Comm." />
        </TabsList>

        <TabsContent value="infos" className="px-5 py-5 mt-0">
          <InfosTab client={client} onSaved={setClient} />
        </TabsContent>
        <TabsContent value="mesures" className="px-4 py-5 mt-0">
          <MesuresTab client={client} onChange={setClient} />
        </TabsContent>
        <TabsContent value="photos" className="px-4 py-5 mt-0">
          <PhotosTab clientId={id} />
        </TabsContent>
        <TabsContent value="commandes" className="px-4 py-5 mt-0">
          <CommandesTab clientId={id} client={client} tailleur={tailleur} />
        </TabsContent>
      </Tabs>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} client={client} atelier={tailleur?.atelier} />

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="rounded-2xl max-w-[340px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Toutes les mesures, photos et commandes de <b>{client.nom}</b> seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete" className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="rounded-full bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TabTrig({ value, icon: Icon, label }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={`tab-${value}`}
      className="flex-col gap-0.5 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:text-tp-green data-[state=active]:border-b-2 data-[state=active]:border-tp-green text-tp-muted"
    >
      <Icon size={19} />
      <span className="text-xs font-semibold">{label}</span>
    </TabsTrigger>
  );
}

// ---------------- INFOS ----------------
function InfosTab({ client, onSaved }) {
  const [nom, setNom] = useState(client.nom);
  const [telephone, setTelephone] = useState(client.telephone || "+221");
  const [sexe, setSexe] = useState(client.sexe);
  const [note, setNote] = useState(client.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!nom.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      const updated = await updateClient(client.id, { nom, telephone: telephone === "+221" ? "" : telephone, sexe, note });
      onSaved(updated);
      toast.success("Modifications enregistrées");
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 tp-fade-up">
      <FieldBox label="Nom complet *">
        <input data-testid="edit-nom" value={nom} onChange={(e) => setNom(e.target.value)} className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green" />
      </FieldBox>
      <FieldBox label="Téléphone">
        <PhoneInput value={telephone} onChange={setTelephone} testId="edit-phone" />
      </FieldBox>
      <FieldBox label="Sexe">
        <div className="grid grid-cols-2 gap-2">
          {[{ v: "H", l: "Homme" }, { v: "F", l: "Femme" }].map((o) => (
            <button key={o.v} data-testid={`edit-sexe-${o.v}`} onClick={() => setSexe(o.v)}
              className={`h-12 rounded-xl border font-semibold ${sexe === o.v ? "bg-tp-green text-white border-tp-green" : "bg-white text-tp-ink border-gray-300"}`}>
              {o.l}
            </button>
          ))}
        </div>
      </FieldBox>
      <FieldBox label="Note">
        <textarea data-testid="edit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          className="w-full rounded-xl bg-white border border-gray-300 px-4 py-3 text-base outline-none focus:border-tp-green resize-none" />
      </FieldBox>
      <Button data-testid="save-infos-btn" onClick={save} disabled={saving} className="w-full h-[52px] rounded-full bg-tp-green hover:bg-tp-greenDark font-bold text-base">
        {saving ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer"}
      </Button>
    </div>
  );
}

function FieldBox({ label, children }) {
  return (
    <div>
      <label className="font-semibold text-tp-ink block mb-1.5 text-sm">{label}</label>
      {children}
    </div>
  );
}

// ---------------- MESURES ----------------
function MesuresTab({ client, onChange }) {
  const [mesures, setMesures] = useState(client.mesures || {});
  const [savedAt, setSavedAt] = useState(client.updatedAt);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const timer = useRef(null);
  const sections = visibleSections(client.sexe);

  const persist = useCallback((next) => {
    setStatus("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const updated = await saveMesures(client.id, next);
        setSavedAt(updated.updatedAt);
        onChange((c) => ({ ...c, mesures: next, updatedAt: updated.updatedAt }));
        setStatus("saved");
        setTimeout(() => setStatus("idle"), 1500);
      } catch {
        setStatus("idle");
      }
    }, 700);
  }, [client.id, onChange]);

  const setField = (key, value) => {
    const next = { ...mesures, [key]: value };
    if (value === "") delete next[key];
    setMesures(next);
    persist(next);
  };

  const setCustom = (idx, patch) => {
    const custom = [...(mesures.custom || [])];
    custom[idx] = { ...custom[idx], ...patch };
    const next = { ...mesures, custom };
    setMesures(next);
    persist(next);
  };
  const addCustom = () => {
    const next = { ...mesures, custom: [...(mesures.custom || []), { nom: "", valeur: "" }] };
    setMesures(next);
  };
  const removeCustom = (idx) => {
    const custom = [...(mesures.custom || [])];
    custom.splice(idx, 1);
    const next = { ...mesures, custom };
    setMesures(next);
    persist(next);
  };

  const formatDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };

  return (
    <div className="tp-fade-up">
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-sm text-tp-muted">Maj le {formatDate(savedAt)}</p>
        <span className="flex items-center gap-1.5 text-sm font-semibold" data-testid="mesures-save-status">
          {status === "saving" ? <><Loader2 size={15} className="animate-spin text-tp-gold" /> <span className="text-tp-gold">Sauvegarde…</span></>
            : status === "saved" ? <><Check size={15} className="text-tp-green" /> <span className="text-tp-green">Enregistré</span></>
            : <span className="text-tp-muted">Sauvegarde auto</span>}
        </span>
      </div>

      <Accordion type="multiple" defaultValue={["haut"]} className="space-y-3">
        {sections.map((s) => {
          const Icon = ICONS[s.icon] || Ruler;
          const filled = s.fields.filter((f) => mesures[f.key] !== undefined && mesures[f.key] !== "").length;
          return (
            <AccordionItem key={s.key} value={s.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <AccordionTrigger data-testid={`section-${s.key}`} className="px-4 py-3.5 hover:no-underline">
                <span className="flex items-center gap-2.5 font-head font-bold text-tp-ink text-base">
                  <Icon size={20} className="text-tp-green" /> {s.label.split(" (")[0]}
                  {filled > 0 && <span className="text-xs bg-tp-green/10 text-tp-green rounded-full px-2 py-0.5 font-semibold">{filled}</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-2.5">
                  {s.fields.map((f) => (
                    <MeasureRow key={f.key} label={f.label} value={mesures[f.key] ?? ""} onChange={(v) => setField(f.key, v)} testId={`mesure-${f.key}`} />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Custom measures */}
      <div className="mt-3 bg-white rounded-xl border border-gray-200 p-4">
        <p className="font-head font-bold text-tp-ink text-base mb-3 flex items-center gap-2"><Plus size={18} className="text-tp-green" /> Mesures personnalisées</p>
        <div className="space-y-2.5">
          {(mesures.custom || []).map((c, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input data-testid={`custom-nom-${idx}`} value={c.nom} onChange={(e) => setCustom(idx, { nom: e.target.value })} placeholder="Nom (ex: Tour poitrine bas)"
                className="flex-1 h-11 rounded-xl bg-white border border-gray-300 px-3 text-base outline-none focus:border-tp-green" />
              <input data-testid={`custom-val-${idx}`} inputMode="decimal" value={c.valeur} onChange={(e) => setCustom(idx, { valeur: e.target.value })} placeholder="cm"
                className="w-20 h-11 rounded-xl bg-white border border-gray-300 px-3 text-base text-center outline-none focus:border-tp-green" />
              <button data-testid={`custom-del-${idx}`} onClick={() => removeCustom(idx)} className="w-10 h-11 flex items-center justify-center text-red-500"><X size={18} /></button>
            </div>
          ))}
        </div>
        <button data-testid="add-custom-btn" onClick={addCustom} className="mt-3 w-full h-11 rounded-xl border-2 border-dashed border-tp-green/40 text-tp-green font-semibold flex items-center justify-center gap-2">
          <Plus size={18} /> Ajouter une mesure
        </button>
      </div>
    </div>
  );
}

function MeasureRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-base text-tp-ink flex-1">{label}</span>
      <div className="relative">
        <input
          data-testid={testId}
          inputMode="decimal"
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="—"
          className="w-24 h-12 rounded-xl bg-tp-bg border border-gray-300 pl-3 pr-8 text-base font-semibold text-center outline-none focus:border-tp-green focus:bg-white"
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-tp-muted">cm</span>
      </div>
    </div>
  );
}

// ---------------- PHOTOS ----------------
function PhotoImg({ id, className, onClick }) {
  const src = `${API}/photos/${id}/download?auth=${getToken()}`;
  return <img data-testid={`photo-${id}`} src={src} alt="" loading="lazy" onClick={onClick} className={className} />;
}

function PhotosTab({ clientId }) {
  const { online } = useSync();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [viewer, setViewer] = useState(null);
  const [pendingCategorie, setPendingCategorie] = useState("tissu");
  const camRef = useRef(null);
  const galRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/photos`);
      setPhotos(data);
    } catch {
      /* offline */
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load, online]);

  const handleFile = async (file, categorie) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024 && !file.type.startsWith("image/")) {
      return toast.error("Fichier trop lourd (max 5 Mo)");
    }
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const { photo, queued } = await uploadPhoto(clientId, compressed, categorie, "");
      if (queued) {
        setQueuedCount((n) => n + 1);
        toast.info("Photo en attente d'envoi (hors ligne)");
      } else {
        setPhotos((p) => [photo, ...p]);
        toast.success("Photo ajoutée");
      }
    } catch (e) {
      toast.error(e?.response?.status === 402 ? "Limite gratuite atteinte (50 photos)." : "Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const pick = (ref, categorie) => { setPendingCategorie(categorie); ref.current.value = ""; ref.current.click(); };

  const delPhoto = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      setViewer(null);
      toast.success("Photo supprimée");
    } catch { toast.error("Erreur"); }
  };

  const clientPhotos = photos.filter((p) => p.categorie === "client");
  const tissuPhotos = photos.filter((p) => p.categorie !== "client");

  return (
    <div className="tp-fade-up">
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files[0], pendingCategorie)} data-testid="camera-input" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0], pendingCategorie)} data-testid="gallery-input" />

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button data-testid="take-photo-btn" onClick={() => pick(camRef, "tissu")} disabled={uploading}
          className="h-12 rounded-xl bg-tp-green text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          <Camera size={20} /> Prendre
        </button>
        <button data-testid="pick-photo-btn" onClick={() => pick(galRef, "tissu")} disabled={uploading}
          className="h-12 rounded-xl bg-white border border-tp-green text-tp-green font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          <ImageIcon size={20} /> Galerie
        </button>
      </div>

      {uploading && <div className="flex items-center gap-2 text-tp-gold text-sm mb-3"><Loader2 size={16} className="animate-spin" /> Compression et envoi…</div>}
      {queuedCount > 0 && <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-sm mb-3"><Clock size={16} /> {queuedCount} photo(s) en attente d'envoi</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-tp-green" /></div>
      ) : photos.length === 0 ? (
        <div className="text-center py-10 text-tp-muted">
          <ImageIcon size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-base">Aucune photo. Ajoutez le client ou ses tissus.</p>
        </div>
      ) : (
        <>
          <PhotoGroup title="Photo du client" photos={clientPhotos} onOpen={setViewer} onAddClient={() => pick(galRef, "client")} showAdd={clientPhotos.length === 0} />
          <PhotoGroup title="Tissus" photos={tissuPhotos} onOpen={setViewer} />
        </>
      )}

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-md p-0 bg-black rounded-2xl overflow-hidden">
          {viewer && (
            <div>
              <PhotoImg id={viewer.id} className="w-full max-h-[70vh] object-contain" />
              <div className="p-4 bg-white flex gap-2">
                <a data-testid="download-photo-btn" href={`${API}/photos/${viewer.id}/download?auth=${getToken()}`} download target="_blank" rel="noreferrer"
                  className="flex-1 h-12 rounded-full bg-tp-green text-white font-semibold flex items-center justify-center gap-2">
                  <Download size={18} /> Télécharger
                </a>
                <button data-testid="delete-photo-btn" onClick={() => delPhoto(viewer.id)} className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoGroup({ title, photos, onOpen, onAddClient, showAdd }) {
  if (photos.length === 0 && !showAdd) return null;
  return (
    <div className="mb-5">
      <p className="text-xs text-tp-muted uppercase tracking-wider font-semibold mb-2">{title}</p>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
            <PhotoImg id={p.id} className="w-full h-full object-cover cursor-pointer" onClick={() => onOpen(p)} />
          </div>
        ))}
        {showAdd && (
          <button onClick={onAddClient} data-testid="add-client-photo" className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-tp-muted">
            <Plus size={22} /><span className="text-xs mt-1">Ajouter</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------- COMMANDES ----------------
function CommandesTab({ clientId, client, tailleur }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [prix, setPrix] = useState("");
  const [statut, setStatut] = useState("en_cours");
  const [saving, setSaving] = useState(false);
  const [payFor, setPayFor] = useState(null); // {amount} or {} for general

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/commandes`);
      setItems(data);
    } catch { /* offline */ } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!type.trim()) return toast.error("Indiquez le type de tenue");
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/commandes`, { type, prix: prix ? Number(prix) : null, statut });
      setItems((i) => [data, ...i]);
      setOpen(false); setType(""); setPrix(""); setStatut("en_cours");
      toast.success("Commande ajoutée");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const cycleStatut = async (o) => {
    const order = ["en_cours", "termine", "livre"];
    const next = order[(order.indexOf(o.statut) + 1) % order.length];
    try {
      const { data } = await api.put(`/commandes/${o.id}`, { type: o.type, prix: o.prix, statut: next, photo_id: o.photo_id });
      setItems((i) => i.map((x) => (x.id === o.id ? data : x)));
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => {
    try { await api.delete(`/commandes/${id}`); setItems((i) => i.filter((x) => x.id !== id)); } catch { toast.error("Erreur"); }
  };

  const notifyReady = (o) => {
    if (!client?.telephone) { toast.error("Ce client n'a pas de numéro de téléphone"); return; }
    const atelier = tailleur?.atelier || tailleur?.nom || "";
    const msg = [
      `🧵 Bonjour ${client?.nom || ""},`,
      `Votre commande *${o.type}* est prête ! ✅`,
      `Vous pouvez venir la récupérer${atelier ? ` chez ${atelier}` : ""}.`,
      "",
      "✂️ Envoyé via Tailleur Pro",
    ].join("\n");
    window.open(whatsappUrl(client.telephone, msg), "_blank");
  };

  const statutColor = { en_cours: "bg-amber-100 text-amber-800", termine: "bg-blue-100 text-blue-800", livre: "bg-green-100 text-green-800" };

  return (
    <div className="tp-fade-up">
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Button data-testid="add-commande-btn" onClick={() => setOpen(true)} className="h-12 rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
          <Plus size={20} /> Commande
        </Button>
        <Button data-testid="encaisser-btn" onClick={() => setPayFor({})} className="h-12 rounded-full bg-tp-gold hover:bg-tp-gold/90 text-tp-ink font-bold">
          <Wallet size={20} /> Encaisser
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-tp-green" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-tp-muted"><Package size={40} className="mx-auto mb-3 opacity-40" /><p className="text-base">Aucune commande</p></div>
      ) : (
        <div className="space-y-2.5">
          {items.map((o) => (
            <div key={o.id} data-testid={`commande-${o.id}`} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-tp-ink">{o.type}</p>
                  <p className="text-sm text-tp-muted">
                    {new Date(o.date).toLocaleDateString("fr-FR")}{o.prix ? ` · ${o.prix.toLocaleString("fr-FR")} FCFA` : ""}
                  </p>
                </div>
                <button data-testid={`statut-${o.id}`} onClick={() => cycleStatut(o)} className={`text-xs font-semibold rounded-full px-3 py-1.5 ${statutColor[o.statut]}`}>
                  {statutLabel(o.statut)}
                </button>
                <button onClick={() => del(o.id)} className="text-red-400"><Trash2 size={18} /></button>
              </div>
              <div className="mt-3 space-y-2">
                {o.statut === "termine" && (
                  <button
                    data-testid={`notify-${o.id}`}
                    onClick={() => notifyReady(o)}
                    className="w-full h-10 rounded-lg bg-tp-whatsapp/15 text-tp-ink font-semibold text-sm flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform"
                  >
                    <BellRing size={16} className="text-tp-whatsapp" /> Prévenir le client (commande prête)
                  </button>
                )}
                {o.statut !== "livre" && (
                  <button
                    data-testid={`encaisser-${o.id}`}
                    onClick={() => setPayFor({ amount: o.prix || "" })}
                    className="w-full h-10 rounded-lg bg-tp-gold/15 text-tp-ink font-semibold text-sm flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform"
                  >
                    <Wallet size={16} className="text-tp-gold" /> Encaisser ce paiement
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-xl">Nouvelle commande</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-1">
            <FieldBox label="Type de tenue *">
              <input data-testid="commande-type" value={type} onChange={(e) => setType(e.target.value)} placeholder="Ex: Boubou, Costume, Abaya, Kaftan"
                className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green" autoFocus />
            </FieldBox>
            <FieldBox label="Prix (FCFA)">
              <input data-testid="commande-prix" inputMode="numeric" value={prix} onChange={(e) => setPrix(e.target.value.replace(/[^\d]/g, ""))} placeholder="Optionnel"
                className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green" />
            </FieldBox>
            <FieldBox label="Statut">
              <div className="grid grid-cols-3 gap-2">
                {STATUTS.map((s) => (
                  <button key={s.value} data-testid={`set-statut-${s.value}`} onClick={() => setStatut(s.value)}
                    className={`h-11 rounded-xl border text-sm font-semibold ${statut === s.value ? "bg-tp-green text-white border-tp-green" : "bg-white text-tp-ink border-gray-300"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </FieldBox>
            <Button data-testid="save-commande-btn" onClick={add} disabled={saving} className="w-full h-[52px] rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
              {saving ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentDialog
        open={!!payFor}
        onOpenChange={(o) => !o && setPayFor(null)}
        tailleur={tailleur}
        defaultAmount={payFor?.amount || ""}
        clientName={client?.nom}
        clientPhone={client?.telephone}
      />
    </div>
  );
}

// ---------------- SHARE ----------------
function ShareDialog({ open, onOpenChange, client, atelier }) {
  const [customNumber, setCustomNumber] = useState("+221");
  const [mode, setMode] = useState("client"); // client | other
  const message = buildMesuresMessage(client, atelier);
  const empty = !hasAnyMesure(client);

  const sendTo = (phone) => {
    window.open(whatsappUrl(phone, message), "_blank");
    onOpenChange(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Mesures copiées"); }
    catch { toast.error("Copie impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Send size={20} className="text-tp-whatsapp" /> Partager les mesures</DialogTitle></DialogHeader>
        {empty && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Aucune mesure renseignée pour l'instant.</p>}
        <pre data-testid="share-preview" className="bg-tp-bg rounded-xl p-3 text-sm text-tp-ink whitespace-pre-wrap max-h-52 overflow-auto border border-gray-200 font-body">{message}</pre>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="mode-client" onClick={() => setMode("client")} className={`h-11 rounded-xl border font-semibold text-sm ${mode === "client" ? "bg-tp-green text-white border-tp-green" : "bg-white border-gray-300"}`}>Au client</button>
          <button data-testid="mode-other" onClick={() => setMode("other")} className={`h-11 rounded-xl border font-semibold text-sm ${mode === "other" ? "bg-tp-green text-white border-tp-green" : "bg-white border-gray-300"}`}>Autre numéro</button>
        </div>
        {mode === "other" && <PhoneInput value={customNumber} onChange={setCustomNumber} testId="share-phone" />}
        <Button
          data-testid="send-whatsapp-btn"
          onClick={() => sendTo(mode === "client" ? client.telephone : customNumber)}
          className="w-full h-[52px] rounded-full bg-tp-whatsapp hover:brightness-105 font-bold text-base"
        >
          <Send size={20} /> Ouvrir WhatsApp
        </Button>
        <Button data-testid="copy-mesures-btn" onClick={copy} variant="outline" className="w-full h-12 rounded-full border-tp-green text-tp-green font-semibold">
          <Copy size={18} /> Copier les mesures
        </Button>
      </DialogContent>
    </Dialog>
  );
}
